import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button, Container, Form } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fi } from 'date-fns/locale'; // the locale you want
import { Loader, SectionHeader, addStatusToast } from '../../common/components';
import { useAppDispatch } from '../../app/hooks';
import {
  GetSalesStatisticsResponse,
  getOwnersWhoWantPostBookingsReportReportAsync,
  OwnerReportFilter,
} from './statisticsSlice';
import { LOCALES } from '../../common/constants';
import { useLocale } from '../../locales/useLocale';
// Register once in your app bootstrap (or in the component before usage)
registerLocale('fi', fi);
interface DateRange {
  from: Date;
  to: Date;
}

function OwnerReport() {
  const dispatch = useAppDispatch();
  const { currentLocale } = useLocale();
  const currentLanguage = currentLocale === LOCALES.Fi ? 'fi' : 'en';
  const [loading, setLoading] = useState<boolean>(false);

  function getLastTuesday(): Date {
    const today = new Date();
    const dayOfWeek = today.getDay(); // Sunday=0, Monday=1, Tuesday=2, ...
    const daysSinceTuesday = (dayOfWeek + 7 - 2) % 7 || 7;
    // if today is Tuesday, go back 7 days (not 0)

    const lastTuesday = new Date(today);
    lastTuesday.setDate(today.getDate() - daysSinceTuesday);
    return lastTuesday;
  }

  function getTuesdayBeforeLast(): Date {
    const lastTuesday = getLastTuesday();
    const previousTuesday = new Date(lastTuesday);
    previousTuesday.setDate(lastTuesday.getDate() - 7);
    return previousTuesday;
  }

  const lastTuesday = getLastTuesday();
  const tuesdayBeforeLast = getTuesdayBeforeLast();

  // const today = new Date();
  // const oneMonthAgo = new Date();

  const [startDate, setStartDate] = useState(tuesdayBeforeLast);
  const [endDate, setEndDate] = useState(lastTuesday);
  // oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const [dayPickerSelectedRange, setDayPickerSelectedRange] =
    useState<DateRange>({
      from: tuesdayBeforeLast,
      to: lastTuesday,
    });

  useEffect(() => {
    // declare the data fetching function
    const fetchData = async () => {};

    // call the function
    fetchData()
      // make sure to catch any error
      .catch(console.error);
  }, []);

  const loadStatistics = async () => {
    setLoading(true);

    const filter = {} as OwnerReportFilter;
    filter.bookingsCreatedAtOrAfterDate = dayPickerSelectedRange.from;
    filter.bookingsStartAfterDate = dayPickerSelectedRange.to;

    dispatch(getOwnersWhoWantPostBookingsReportReportAsync(filter))
      .then(action => {
        setLoading(false);
        const response = action.payload as GetSalesStatisticsResponse;

        if (response.status === 200) {
          const link = document.createElement('a');
          link.href = response.blobUrl;
          link.setAttribute('download', response.fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        } else if (response.status === 204) {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Hakuehdoilla ei löytynyt varauksia`,
            }),
          );
        } else {
          dispatch(
            addStatusToast({
              Title: 'Virhe',
              IsFailed: true,
              Message: `Raportin lataus ei onnistunut`,
            }),
          );
        }
      })
      .catch(error => {
        setLoading(false);
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            IsFailed: true,
            Message: `Raportin lataus ei onnistunut`,
          }),
        );
      });
  };

  const setAccommodationDropdownDateRange = (
    startDateSelected: Date,
    endDateSelected: Date,
  ) => {
    const selectedRange = dayPickerSelectedRange;
    if (startDateSelected !== undefined) {
      selectedRange.from = startDateSelected;
      setStartDate(startDateSelected);
      // Move enddate, if selected startdate is greater than endDate
      if (endDate < startDateSelected) {
        setEndDate(startDateSelected);
      }
    }
    if (endDateSelected !== undefined) {
      selectedRange.to = endDateSelected;
      setEndDate(endDateSelected);
    }
    setDayPickerSelectedRange(selectedRange);
  };

  return (
    <>
      <Helmet>
        <title>Omistaja raportti</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Omistaja raportti']}
          Title="Omistaja raportti"
        />

        <div className="bg-white p-5 position-relative">
          <div className="row gx-48 gy-24">
            <div className="col-6">
              <Form.Label htmlFor="createdFrom" className="form-label">
                Varaukset luotu päivänä tai jälkeen
              </Form.Label>
              <div className="d-flex align-items-center">
                <DatePicker
                  locale={currentLanguage}
                  selected={startDate}
                  onChange={date =>
                    setAccommodationDropdownDateRange(date, undefined)
                  }
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  className="form-control"
                  dateFormat="dd.MM.yyyy"
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
              <Form.Label htmlFor="createdFrom" className="form-label">
                Varaukset alkavat päivänä tai jälkeen
              </Form.Label>
              <div className="d-flex align-items-center">
                <DatePicker
                  locale={currentLanguage}
                  selected={endDate}
                  onChange={date =>
                    setAccommodationDropdownDateRange(undefined, date)
                  }
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  className="form-control"
                  dateFormat="dd.MM.yyyy"
                />
                <Button
                  variant="link"
                  className="pe-none position-absolute end-0 top-0"
                  id="button-addon2"
                >
                  <span className="bi bi-calendar2-event" />
                </Button>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-start gap-16">
            <button
              type="button"
              onClick={loadStatistics}
              className="btn btn-primary mt-32"
              disabled={loading}
            >
              <span>Lataa</span>
            </button>
            {loading && <Loader IsLoading />}
          </div>
        </div>
      </Container>
    </>
  );
}

export default OwnerReport;
